import './Register.scss';

import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useEffect, useReducer, useState } from 'react';
import { Form, Modal, Spinner } from 'react-bootstrap';
import InputGroup from 'react-bootstrap/InputGroup';
import { useCookies } from 'react-cookie';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { PSButton } from '@/components/ui/button';

import { useAppDispatch } from '../../app/hook';
import HttpService from '../../app/http';
import { useLoginMutation, useSocialAuthLoginMutation } from '../../app/services/auth-rtk.service';
import Alert, { IAlertType } from '../../components/common/Alert';
import { AlertsType } from '../../constants/alerts.constant';
import { MultiLanguageUrl } from '../../constants/multiLanguage.constant';
import { login as authSliceLogin, setError } from '../../features/auth/auth.slice';
import useAppLocalStorage from '../../hooks/useAppLocalStorage';
import { countries } from '../../utils/countries';
import { SumUserTypeEnum } from '../../utils/SumUserTypeEnum';
import TOSBusinessPartner from '../site/TOSBusinessPartner';
import { WrappedErrorBoundary } from '../WrappedErrorBoundary';
import Timer from './Timer';
import { withSocialAuth } from './withSocialAuth';

interface IBussinessPartnerDetail {
  firstName: string;
  lastName: string;
  companyName: string;
  birthDate: string;
  address1: string;
  address2: string;
  zipCode: string;
  city: string;
  country: string;
  phone: string;
  websiteLinks: string;
  promotionPlan: string;
  monthlyViews: string;
  ambitions: string;
  textReaded: boolean;
  termsAndConditions: boolean;
}

interface IBussinessPartnerRegistration {
  partnerInfo: IBussinessPartnerDetail;
  user_email?: string;
  adminPanel: boolean;
  user_password?: string;
}

interface IStep2FormData {
  websiteLinks: string;
  promotionPlan: string;
  monthlyViews: string;
  ambitions: string;
}

interface IFormData {
  firstName: string;
  lastName: string;
  companyName: string;
  address1: string;
  address2?: string;
  city: string;
  country: string;
  state?: string;
  zipcode: string;
  phone?: string;
  cookieConsent: boolean;
  birthDate: string;
}

interface IStep3 {
  textReaded: boolean;
  termsAndConditions: boolean;
}

type state = {
  currentStep: number;
  step: {
    title: string;
    description: string;
    data?: IFormData | IStep2FormData | IStep3;
  }[];
};

type Action = { type: 'SetStep'; payload: number } | { type: 'NextStep'; payload: IFormData | IStep2FormData | any } | { type: 'PreviesStep' };

// Reducer function
function reducer(state: state, action: Action): state {
  switch (action.type) {
    case 'SetStep':
      return { ...state, currentStep: action.payload };
    case 'NextStep':
      state.step[state.currentStep - 1] = {
        ...state.step[state.currentStep - 1],
        data: action.payload,
      };
      return {
        ...state,
        currentStep: state.currentStep + 1,
      };
    case 'PreviesStep':
      return { ...state, currentStep: state.currentStep - 1 };
    default:
      return state;
  }
}

const BusinessInformation = (props: any) => {
  const [showConfirmEmailModal, setShowConfirmEmailModal] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [showTimer, setShowTimer] = useState(false);
  const [loginUser, LoginUserMutation] = useLoginMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [cookies] = useCookies(['utm_datas', '_fbc', '_fbp']);
  const [socialAuthLogin, { isLoading: isSocialAuthLoading }] = useSocialAuthLoginMutation();
  const [toast, setToast] = useState<IAlertType>({
    isShow: false,
    title: '',
    message: '',
    type: 'Error',
  });
  const [toastConfirm, setToastConfirm] = useState<IAlertType>({
    isShow: false,
    title: '',
    message: '',
    type: 'Error',
  });

  const RegisterBussinessPartnerMutation = useMutation<any, AxiosError<any, any>, any>(data => HttpService.post('/partner/bussiness-partner/register', data));

  const closeToastConfirm = () => {
    setToastConfirm({
      isShow: false,
      message: '',
      title: '',
      type: 'warning',
    });
  };

  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const localStorage = useAppLocalStorage();

  const applicationDispatch = useAppDispatch();

  const [state, dispatch] = useReducer(reducer, {
    currentStep: 1,
    step: [
      {
        title: t('BusinessInformation/Steps/Step1/title'), // Translate the title dynamically
        description: t('BusinessInformation/Steps/Step1/description'),
      },
      {
        title: t('BusinessInformation/Steps/Step2/title'), // Translate the title dynamically
        description: t('BusinessInformation/Steps/Step2/description'), // Translate description dynamically
      },
      {
        title: t('BusinessInformation/Steps/Step3/title'), // Translate the title dynamically
        description: t('BusinessInformation/Steps/Step3/description'), // Translate description dynamically
      },
    ],
  });

  const handleClose = () => setShowConfirmEmailModal(false);

  const onNextStep = (data: IFormData | IStep2FormData | IStep3) => {
    let registerData: {
      user_email: string;
      user_password: string;
      user_confirm_password: string;
      user_utm_data?: any;
    } | null = null;

    let socialAuth: {
      type: string;
      providerPayload: {
        access_token: string;
        token_type: string;
        expires_in: number;
        scope: string;
        authuser: string;
        hd: string;
        prompt: string;
        provider: string;
      };
      user_utm_data: any;
    } | null = null;

    if (state.currentStep == 3 && isLoading === false) {
      setIsLoading(true);

      const businessPartnerRegisterData: any = sessionStorage.getItem('business-partner');

      if (businessPartnerRegisterData) {
        const data = JSON.parse(businessPartnerRegisterData);
        if (data?.register) {
          registerData = data?.register;
        }
        if (data?.socialAuth) {
          socialAuth = data?.socialAuth;
        }
      } else {
        navigate(`/${MultiLanguageUrl[i18n.language] || 'en-US'}/partner/business/register`, {
          replace: true,
        });
      }

      state.step[2].data = data;

      const step1 = state.step[0].data as IFormData;
      const step2 = state.step[1].data as IStep2FormData;
      const step3 = state.step[2].data as IStep3;

      const partnerInfo = {
        address1: step1.address1,
        address2: step1.address2,
        birthDate: step1.birthDate,
        city: step1.city,
        companyName: step1.companyName,
        country: step1.country,
        firstName: step1.firstName,
        lastName: step1.lastName,
        phone: step1.phone,
        state: step1.state,
        monthlyViews: step2.monthlyViews,
        textReaded: step3.textReaded,
        promotionPlan: step2.promotionPlan,
        ambitions: step2.ambitions,
        termsAndConditions: step3.termsAndConditions,
        websiteLinks: step2.websiteLinks,
        zipCode: step1.zipcode,
      };

      if (registerData) {
        RegisterBussinessPartnerMutation.mutate(
          {
            partnerInfo: partnerInfo,
            user_email: registerData?.user_email,
            user_password: registerData?.user_password,
            adminPanel: false,
          } as IBussinessPartnerRegistration,
          {
            onSuccess: res => {
              setIsLoading(false);

              setShowConfirmEmailModal(true);
              setUserData({
                ...res.data.element,
                user_email: registerData?.user_email,
                user_password: registerData?.user_password,
              });
            },
            onError: res => {
              console.log(res);

              setIsLoading(false);

              if (res?.response?.status && res?.response?.status != 500) {
                setToast({
                  isShow: true,
                  message: res?.response?.data?.message,
                  title: 'Warning',
                  type: 'warning',
                });

                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            },
          }
        );
      } else if (socialAuth) {
        const newData: any = { ...socialAuth };

        if (window.navigator.cookieEnabled && cookies.utm_datas && cookies.utm_datas !== '') {
          newData.user_utm_data = cookies.utm_datas;

          newData.user_utm_data = {
            ...newData.user_utm_data,
            client_user_agent: navigator.userAgent,
          };

          if (cookies.utm_datas.fbclid) {
            if (cookies._fbp && cookies._fbp !== '') {
              newData.user_utm_data = {
                ...newData.user_utm_data,
                fbp: cookies._fbp,
              };
            }

            if (cookies._fbc && cookies._fbc !== '') {
              newData.user_utm_data = {
                ...newData.user_utm_data,
                fbc: cookies._fbc,
              };
            }
          }
        }
        socialAuthLogin({
          ...newData,
          partnerInfo: partnerInfo,
        })
          .unwrap()
          .then(res => {
            localStorage.setItem(`LOUISEBOT_token`, res?.token);

            applicationDispatch(
              authSliceLogin({
                is_remember_me: true,
              })
            );

            navigate(`/${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel`, {
              replace: true,
            });
          })
          .catch(err => {
            setIsLoading(false);
            applicationDispatch(setError(err?.data?.message));

            setToast({
              isShow: true,
              message: err?.data?.message,
              title: 'Warning',
              type: 'warning',
            });
          });
      }
    } else {
      if (state.currentStep < 3) dispatch({ type: 'NextStep', payload: data });
    }
  };

  const ResendEmailVerification = useMutation<any, AxiosError<any, any>>(() => HttpService.post(`/auth/${userData.userIdentification}/validation-link`), {
    onSuccess: () => {
      setShowTimer(true);
    },
    onError: () => {
      setShowTimer(false);
    },
  });

  const handleLogin = () => {
    const data: any = {
      user_identification: userData.userIdentification,
      user_email: userData.user_email,
      user_password: userData.user_password,
    };

    loginUser({
      user_email: data.user_email,
      user_password: data.user_password,
    })
      .unwrap()
      .then(({ token, refresh_token, userAccessToken, ...res }) => {
        localStorage.setItem(`LOUISEBOT_token`, token);
        localStorage.setItem(`LOUISEBOT_refresh_token`, refresh_token);
        localStorage.setItem(`LOUISEBOT_user_access_token`, userAccessToken);

        applicationDispatch(
          authSliceLogin({
            is_remember_me: true,
          })
        );

        if (res?.plan == SumUserTypeEnum.BUSINESSPARTNER) {
          navigate(`/${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel/business-partner-panel`, {
            replace: true,
          });

          return;
        } else {
          navigate(`/${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel`, {
            replace: true,
          });
        }
      })
      .catch(res => {
        if (res?.status && res?.status != 500) {
          let message = '';

          if (res?.data?.message && res?.data?.message == 'Credential error.') {
            message = t(AlertsType.ISATKO010);
          } else {
            message = res?.data?.message;
          }

          setToastConfirm({
            isShow: true,
            message: message || t(AlertsType.ISATKO010),
            title: 'Warning',
            type: 'warning',
          });
        }
      });
  };

  const onPreviusStep = () => {
    dispatch({ type: 'PreviesStep' });
  };

  return (
    <div className=" tw-bg-gray-100 tw-flex tw-flex-col tw-items-center tw-justify-start tw-min-h-screen">
      <div className="tw-w-full tw-h-16">
        <Link to="https://louisebot.com" className="tw-justify-center tw-py-4 tw-gap-1 tw-flex tw-items-center">
          <img src="https://cdn.1cdn.app/application/LOUISEBOT/23091707385795_Logo_colorful.svg" className="tw-w-10 tw-h-10 tw-relative" alt="logo" />
          <div className="tw-text-blue-700 tw-text-lg tw-font-extrabold tw-font-['Lexend'] tw-leading-7">LouiseBot</div>
        </Link>
      </div>
      <div className=" md:tw-px-8 tw-px-2 tw-pt-10 tw-pb-6 md:tw-w-full  tw-max-w-[1128px] tw-bg-white tw-rounded-2xl tw-flex-col tw-justify-start tw-items-center tw-gap-8 tw-inline-flex">
        <div className="tw-flex-col tw-justify-start tw-items-start md:tw-gap-16 tw-gap-4 tw-flex">
          <div className="tw-self-stretch  tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-flex">
            <div className="tw-self-stretch tw-text-center tw-text-gray-800 tw-text-4xl tw-font-bold tw-font-['Lexend'] tw-leading-10">
              {t('authPages/register/sign-up')}
            </div>
            <div className="tw-w-full tw-justify-start tw-items-start tw-gap-4 tw-inline-flex">
              {state?.step?.map((item, index) => (
                <div key={index} className="tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-inline-flex">
                  <div className={`tw-self-stretch tw-h-1 ${index + 1 == state.currentStep ? 'tw-bg-primary' : 'tw-bg-gray-300'} tw-rounded-full`} />
                  <div className="tw-flex-col tw-justify-start tw-items-start tw-flex">
                    <div
                      className={`${
                        index + 1 == state.currentStep ? 'tw-text-primary' : ''
                      } tw-text-xs tw-font-medium tw-font-['Lexend'] tw-uppercase tw-leading-none`}
                    >
                      {item.title}
                    </div>
                    <div className="tw-text-gray-900 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{item.description}</div>
                  </div>
                </div>
              ))}
            </div>

            {toast && toast.isShow && (
              <Alert className="tw-w-full" type={toast.type}>
                {toast.message}
              </Alert>
            )}

            {toastConfirm.isShow && (
              <Alert className="tw-flex tw-items-center tw-w-full" type={toastConfirm.type} onClick={() => closeToastConfirm()}>
                {toastConfirm.message}
              </Alert>
            )}
          </div>
          <div className=" tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-inline-flex">
            {state.currentStep == 1 && <Step1 data={state?.step[0]?.data as IFormData} onNext={onNextStep} />}
            {state.currentStep == 2 && <Step2 data={state?.step[1]?.data as IStep2FormData} onNext={onNextStep} onPrevius={onPreviusStep} />}
            {state.currentStep == 3 && (
              <Step3 state={state} data={state?.step[2]?.data as IStep3} onNext={onNextStep} onPrevius={onPreviusStep} isLoading={isLoading} />
            )}
          </div>
        </div>
        <div translate="no" className="tw-text-zinc-500 tw-text-xs tw-font-normal tw-font-['Roboto'] tw-leading-tight">
          © LouiseBot {new Date().getFullYear()}
        </div>
      </div>
      {/* Confirm Email Modal*/}
      {showConfirmEmailModal && (
        <Modal show={showConfirmEmailModal} size="lg" centered onHide={handleClose}>
          <Modal.Header className="border-0 d-flex justify-content-center position-relative" closeButton />
          <Modal.Body className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-gap-4">
            <div className="py-md-3">
              <svg width="280" height="280" viewBox="0 0 280 280" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="280" height="280" rx="140" fill="#F0F4FF" />
                <rect width="280" height="280" rx="140" fill="url(#paint0_radial_1788_7789)" />
                <path
                  d="M140 60C95.888 60 60 95.888 60 140C60 184.112 95.888 220 140 220C184.112 220 220 184.112 220 140C220 95.888 184.112 60 140 60ZM124.008 175.304L94.304 145.664L105.6 134.336L123.992 152.696L166.344 110.344L177.656 121.656L124.008 175.304Z"
                  fill="#4167DA"
                />
                <defs>
                  <radialGradient
                    id="paint0_radial_1788_7789"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(140 140) rotate(90) scale(140)"
                  >
                    <stop stopColor="#D9E2FB" stopOpacity="0" />
                    <stop offset="1" stopColor="#C8D6F8" stopOpacity="0.32" />
                  </radialGradient>
                </defs>
              </svg>
            </div>

            {toastConfirm.isShow && (
              <Alert className="tw-flex tw-items-center" type={toastConfirm.type} onClick={() => closeToastConfirm()}>
                {toastConfirm.message}
              </Alert>
            )}

            <h2 className="font-sofiaSans tw-font-bold ">{t('authPages/register/l-07')}</h2>

            <p className="font-poppins fs-5 tw-text-center">
              {t('authPages/register/text-01')} ({userData?.user_email}). <br />
              {t('authPages/register/text-02')}
            </p>

            <PSButton
              id="btn-business-login"
              className="justify-content-center align-items-center"
              variant="primary"
              onClick={handleLogin}
              disabled={LoginUserMutation.isLoading}
              isLoading={LoginUserMutation.isLoading}
              title={t('authPages/register/btn-02')}
            />

            <PSButton
              id="btn-business-show-timer"
              variant="outline"
              onClick={() => userData.user_email && ResendEmailVerification.mutate(undefined)}
              disabled={showTimer}
              isLoading={ResendEmailVerification.isLoading}
              loadingAnimation="grow"
            >
              {showTimer ? (
                <span className="text-muted">
                  <Timer setShowTimer={setShowTimer} ttl={120} /> <>{t('authPages/register/l-08')}</>
                </span>
              ) : (
                <>{t('authPages/register/btn-03')}</>
              )}
            </PSButton>
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default WrappedErrorBoundary(withSocialAuth(BusinessInformation));

const Step1 = ({ data, onNext }: { data?: IFormData; onNext: (data: IFormData) => void }) => {
  const { i18n, t } = useTranslation();
  const [phoneCode, setPhoneCode] = useState('+1');

  const handleCountryChange = (countryName: string | null) => {
    const selectedCountry = countries.find(country => country.code === countryName);
    setPhoneCode(selectedCountry ? selectedCountry.dial_code : '');
    if (countryName) setValue('country', countryName);
  };

  useEffect(() => {
    if (data) {
      handleCountryChange(data.country);
    } else {
      handleCountryChange('US');
    }
  }, [data]);

  const schema = Yup.object({
    firstName: Yup.string()
      .required(t('BusinessInformation/Step1/fields/firstName/errors/required'))
      .max(30, t('BusinessInformation/Step1/fields/firstName/errors/maxLength')),
    lastName: Yup.string().required(t('BusinessInformation/Step1/fields/lastName/errors/required')),
    companyName: Yup.string().required(t('BusinessInformation/Step1/fields/companyName/errors/required')),
    address1: Yup.string(),
    address2: Yup.string(),
    city: Yup.string(),
    country: Yup.string().required(t('BusinessInformation/Step1/fields/country/errors/required')),
    state: Yup.string(),
    zipcode: Yup.string(),
    phone: Yup.string(),
    birthDate: Yup.string().required(t('BusinessInformation/Step1/fields/birthDate/errors/required')),
  });

  const {
    register,
    formState: { errors },
    reset,
    setValue,
    handleSubmit,
    setError,
    clearErrors,
    watch,
  } = useForm<IFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: data ?? {},
  });

  const onSubmit = (data: IFormData) => {
    onNext(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className=" tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-inline-flex">
      <div className=" tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-inline-flex">
        <div className="tw-text-gray-900 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{t('BusinessInformation/Step1/title')}</div>
        <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-6 tw-inline-flex">
          <div className="tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-inline-flex">
            <Form.Group className="tw-w-full">
              <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t('BusinessInformation/Step1/fields/firstName/label')}
              </Form.Label>
              <Form.Control
                isInvalid={!!errors.firstName}
                maxLength={60}
                {...register('firstName')}
                className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
              />
              <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                {errors.firstName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </div>
          <div className="tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-inline-flex">
            <Form.Group className="tw-w-full">
              <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t('BusinessInformation/Step1/fields/lastName/label')}
              </Form.Label>
              <Form.Control
                isInvalid={!!errors.lastName}
                {...register('lastName')}
                className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
              />
              <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                {errors.lastName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </div>
        </div>
        <div className="tw-w-full tw-self-stretch tw-justify-start tw-items-start tw-gap-6 tw-inline-flex">
          <div className="tw-w-full tw-self-stretch  tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-flex">
            <Form.Group className="tw-w-full">
              <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t('BusinessInformation/Step1/fields/companyName/label')}
              </Form.Label>
              <Form.Control
                isInvalid={!!errors.companyName}
                {...register('companyName')}
                className="tw-w-full tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
              />
              <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                {errors.companyName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </div>
          <div className="tw-w-full tw-self-stretch  tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-flex">
            <Form.Group className="tw-w-full">
              <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t('BusinessInformation/Step1/fields/birthDate/label')}
              </Form.Label>
              <Form.Control
                isInvalid={!!errors.birthDate}
                type="date"
                {...register('birthDate')}
                className="tw-w-full tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
              />
              <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                {errors.birthDate?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </div>
        </div>
        <div className="tw-self-stretch  tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-flex">
          <Form.Group className="tw-w-full">
            <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {t('BusinessInformation/Step1/fields/address1/label')}
            </Form.Label>
            <Form.Control
              isInvalid={!!errors.address1}
              {...register('address1')}
              className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
            />
            <Form.Control.Feedback type="invalid" className="d-block font-poppins">
              {errors.address1?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </div>

        <div className="tw-self-stretch  tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-flex">
          <Form.Group className="tw-w-full">
            <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {t('BusinessInformation/Step1/fields/address2/label')}
            </Form.Label>
            <Form.Control
              {...register('address2')}
              className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
            />
          </Form.Group>
        </div>
        <div className="tw-self-stretch  tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-flex">
          <Form.Group className="tw-w-full">
            <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {t('BusinessInformation/Step1/fields/country/label')}
            </Form.Label>
            <Form.Select
              translate="no"
              defaultValue={data?.country ?? 'US'}
              isInvalid={!!errors.country}
              {...(register('country'),
              {
                onChange: e => {
                  handleCountryChange(e?.target?.value);
                },
              })}
              className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
            >
              <option value="US">United States</option>
              {countries.map((country: { name: string; dial_code: string; code: string }) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid" className="d-block font-poppins">
              {errors.country?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </div>

        <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-6  tw-flex-col">
          <div className="tw-basis-0 tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-inline-flex tw-w-full">
            <Form.Group className="tw-w-full">
              <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t('BusinessInformation/Step1/fields/zipcode/label')}
              </Form.Label>
              <Form.Control
                isInvalid={!!errors.zipcode}
                {...register('zipcode')}
                className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
              />
              <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                {errors.zipcode?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </div>
          <div className="tw-self-stretch  tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-flex">
            <Form.Group className="tw-w-full">
              <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t('BusinessInformation/Step1/fields/city/label')}
              </Form.Label>
              <Form.Control
                isInvalid={!!errors.city}
                {...register('city')}
                className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
              />
              <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                {errors.city?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </div>
        </div>

        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-flex">
          <Form.Group className="tw-w-full">
            <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {t('BusinessInformation/Step1/fields/phone/label')}
            </Form.Label>
            <InputGroup>
              {phoneCode !== '' && <InputGroup.Text id="phone">{phoneCode}</InputGroup.Text>}
              <Form.Control
                isInvalid={!!errors.phone}
                type="number"
                {...register('phone')}
                className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
              />
            </InputGroup>
            <Form.Control.Feedback type="invalid" className="d-block font-poppins">
              {errors.phone?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </div>
      </div>
      <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-4 tw-inline-flex">
        <button
          id="btn-partner-submit"
          type="submit"
          className="tw-cursor-pointer tw-grow tw-shrink tw-basis-0  tw-px-6 tw-py-3 tw-bg-primary tw-rounded-full tw-border-none tw-shadow tw-justify-center tw-items-center tw-flex"
        >
          <div className="tw-text-white tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{t('BusinessInformation/Step1/buttons/next')}</div>
        </button>
      </div>
    </form>
  );
};

const Step2 = ({ data, onNext, onPrevius }: { data?: IStep2FormData; onNext: (data: IStep2FormData) => void; onPrevius: () => void }) => {
  const { i18n, t } = useTranslation();

  const schema = Yup.object({
    websiteLinks: Yup.string()
      .required(t('BusinessInformation/Step2/errors/websiteLinks/required'))
      .max(500, t('BusinessInformation/Step2/errors/websiteLinks/maxLength'))
      .matches(/^https.*/i, t('BusinessInformation/Step2/errors/websiteLinks/required')),
    promotionPlan: Yup.string()
      .required(t('BusinessInformation/Step2/errors/promotionPlan/required'))
      .max(500, t('BusinessInformation/Step2/errors/promotionPlan/maxLength')),
    monthlyViews: Yup.string()
      .required(t('BusinessInformation/Step2/errors/monthlyViews/required'))
      .max(500, t('BusinessInformation/Step2/errors/monthlyViews/maxLength')),
    ambitions: Yup.string()
      .required(t('BusinessInformation/Step2/errors/ambitions/required'))
      .max(500, t('BusinessInformation/Step2/errors/ambitions/maxLength')),
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<IStep2FormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: data ?? {},
  });

  const onSubmit = (data: IStep2FormData) => {
    onNext(data as IStep2FormData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-inline-flex">
      <div className="tw-text-gray-900 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-none">{t('BusinessInformation/Step2/applyMessage')}</div>

      {/* website Links */}
      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-flex">
        <Form.Group className="tw-mb-4 tw-w-full">
          <Form.Label>{t('BusinessInformation/Step2/websiteLabel')}</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            isInvalid={!!errors.websiteLinks}
            {...register('websiteLinks')}
            className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
            placeholder={t('BusinessInformation/Step2/websitePlaceholder')}
          />
          <Form.Control.Feedback type="invalid">{errors.websiteLinks?.message}</Form.Control.Feedback>
        </Form.Group>
      </div>

      {/* Promotion Plan */}
      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-flex">
        <Form.Group className=" tw-w-full">
          <Form.Label>{t('BusinessInformation/Step2/promotionLabel')}</Form.Label>
          <Form.Control
            as="textarea"
            className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
            rows={3}
            isInvalid={!!errors.promotionPlan}
            {...register('promotionPlan')}
            placeholder={t('BusinessInformation/Step2/promotionPlaceholder')}
          />
          <Form.Control.Feedback type="invalid">{errors.promotionPlan?.message}</Form.Control.Feedback>
        </Form.Group>
      </div>

      {/* Monthly Views */}
      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-flex">
        <Form.Group className=" tw-w-full">
          <Form.Label>{t('BusinessInformation/Step2/monthlyViewsLabel')}</Form.Label>
          <Form.Control
            className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
            isInvalid={!!errors.monthlyViews}
            {...register('monthlyViews')}
            placeholder={t('BusinessInformation/Step2/monthlyViewsPlaceholder')}
          />
          <Form.Control.Feedback type="invalid">{errors.monthlyViews?.message}</Form.Control.Feedback>
        </Form.Group>
      </div>

      {/* Referral Source */}
      <div className="tw-self-stretch  tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-flex">
        <Form.Group className="tw-mb-4 tw-w-full">
          <Form.Label>{t('BusinessInformation/Step2/ambitionLabel')}</Form.Label>
          <Form.Control
            className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
            as="textarea"
            rows={4}
            isInvalid={!!errors.ambitions}
            {...register('ambitions')}
            placeholder={t('BusinessInformation/Step2/ambitionPlaceholder')}
          />
          <Form.Control.Feedback type="invalid">{errors.ambitions?.message}</Form.Control.Feedback>
        </Form.Group>
      </div>

      {/* Buttons */}
      <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-4 tw-inline-flex">
        <button
          id="btn-partner-prev"
          onClick={onPrevius}
          className="tw-cursor-pointer tw-grow tw-shrink tw-basis-0 tw-h-14 tw-px-6 tw-py-3 tw-bg-white tw-border-solid tw-rounded-full tw-shadow tw-border tw-border-gray-300 tw-justify-center tw-items-center tw-flex"
        >
          <div className="tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{t('BusinessInformation/Step2/previousButton')}</div>
        </button>
        <button
          id="btn-partner-next"
          type="submit"
          className="tw-cursor-pointer tw-grow tw-shrink tw-basis-0 tw-h-14 tw-px-6 tw-py-3 tw-bg-primary tw-border-none tw-rounded-full tw-shadow tw-justify-center tw-items-center tw-flex"
        >
          <div className="tw-text-white tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{t('BusinessInformation/Step2/nextButton')}</div>
        </button>
      </div>
    </form>
  );
};

const Step3 = ({
  data,
  state,
  onNext,
  onPrevius,
  isLoading,
}: {
  data?: IStep3;
  state: state;
  onNext: (data: IStep3) => void;
  onPrevius: () => void;
  isLoading: boolean;
}) => {
  const { t } = useTranslation(); // Initialize the translation hook

  // Validation schema
  const schema = Yup.object().shape({
    textReaded: Yup.boolean().oneOf([true], t('BusinessInformation/Step3/textReaded/message')).required('Required'),
    termsAndConditions: Yup.boolean().oneOf([true], t('BusinessInformation/Step3/termsAndConditions/message')).required('Required'),
  });

  const {
    register,
    setValue,
    watch,
    formState: { errors },
    handleSubmit,
  } = useForm<IStep3>({
    resolver: yupResolver(schema),
    defaultValues: data,
  });

  const onSubmit = (data: IStep3) => {
    onNext(data);
  };

  return (
    <form className="tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-inline-flex " onSubmit={handleSubmit(onSubmit)}>
      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-flex ">
        <div className="tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-flex">
          <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex lg:tw-w-full tw-w-[calc(100vw-20px)]">
            <div className="tw-text-gray-900 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">Terms and Conditions</div>
            <TOSBusinessPartner
              firstName={(state?.step[0]?.data as IFormData)?.firstName as string}
              lastName={(state?.step[0]?.data as IFormData)?.lastName as string}
              companyName={(state?.step[0]?.data as IFormData)?.companyName as string}
              birthDate={(state?.step[0]?.data as IFormData)?.birthDate as string}
            />
            <div
              onClick={(e: any) => {
                setValue('textReaded', !watch('textReaded'));
              }}
              onKeyDown={e => e.key === 'Enter' && setValue('textReaded', !watch('textReaded'))}
              role="button"
              tabIndex={0}
              className="tw-cursor-pointer tw-h-10 tw-justify-start tw-items-center tw-gap-2 tw-inline-flex"
            >
              <Form.Group>
                <Form.Check isInvalid={!!errors.textReaded} {...register('textReaded')} />
                <Form.Control.Feedback type="invalid">{errors.textReaded?.message}</Form.Control.Feedback>
              </Form.Group>
              <div className="tw-grow tw-shrink tw-basis-0 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                {t('BusinessInformation/Step3/textReaded/text')}
              </div>
            </div>

            <div
              onClick={(e: any) => {
                setValue('termsAndConditions', !watch('termsAndConditions'));
              }}
              onKeyDown={e => e.key === 'Enter' && setValue('termsAndConditions', !watch('termsAndConditions'))}
              role="button"
              tabIndex={0}
              className="tw-cursor-pointer tw-h-10 tw-justify-start tw-items-center tw-gap-2 tw-inline-flex"
            >
              <Form.Group>
                <Form.Check isInvalid={!!errors.termsAndConditions} {...register('termsAndConditions')} />
                <Form.Control.Feedback type="invalid">{errors.termsAndConditions?.message}</Form.Control.Feedback>
              </Form.Group>
              <div className="tw-grow tw-shrink tw-basis-0 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                {t('BusinessInformation/Step3/termsAndConditions/text')}
              </div>
            </div>
          </div>

          <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-4 tw-inline-flex">
            <button
              id="btn-partner-previous"
              onClick={onPrevius}
              className="tw-grow tw-shrink tw-basis-0 tw-h-14  lg:tw-px-6 tw-px-2 lg:tw-py-3 tw-py-1 tw-bg-white tw-rounded-full tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-justify-center tw-items-center tw-flex"
            >
              <div className="tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
                {t('BusinessInformation/Step3/buttons/previous')}
              </div>
            </button>
            <button
              id="btn-partner-start"
              type="submit"
              className="tw-grow tw-shrink tw-basis-0 tw-h-14  lg:tw-px-6 tw-px-2 lg:tw-py-3 tw-py-1 tw-bg-primary tw-border-none tw-rounded-full tw-shadow tw-justify-center tw-items-center tw-flex"
              disabled={isLoading || state.currentStep > 3}
            >
              <div className="tw-text-white tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
                {isLoading ? <Spinner size="sm" /> : <span>{t('BusinessInformation/Step3/buttons/start')}</span>}
              </div>
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};
